import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { createPortal } from "react-dom";

import {
  administrationApi,
  type AdminDirectoryUser,
  type AdminTeamSummary,
} from "@/features/administration/api/administration.api";

type Props = {
  user: AdminDirectoryUser;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
};

export function ManageTeamMembershipDialog({ user, open, onClose, onUpdated }: Props) {
  const [teams, setTeams] = useState<AdminTeamSummary[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const fromUser = new Set((user.teams ?? []).map((t) => t.id));
    setSelected(fromUser);
    setInitial(fromUser);
    setError(null);
    setLoading(true);
    void (async () => {
      try {
        const rows = await administrationApi.listAdminTeams({ limit: 100, page: 1, status: "active" });
        setTeams(rows);
      } catch {
        setError("Could not load teams.");
        setTeams([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, user]);

  const dirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const id of selected) {
      if (!initial.has(id)) return true;
    }
    return false;
  }, [selected, initial]);

  if (!open) return null;

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      const toAdd = [...selected].filter((id) => !initial.has(id));
      const toRemove = [...initial].filter((id) => !selected.has(id));
      for (const teamId of toAdd) {
        await administrationApi.addTeamMember(teamId, user.id);
      }
      for (const teamId of toRemove) {
        await administrationApi.removeTeamMember(teamId, user.id);
      }
      onUpdated();
      onClose();
    } catch {
      setError("Could not update team membership. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function toggle(teamId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-team-membership-title"
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="manage-team-membership-title" className="text-lg font-semibold text-gray-900">
          Manage team membership
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Choose which teams <span className="font-medium text-gray-900">{user.name}</span> belongs to.
        </p>

        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading teams…
          </div>
        ) : teams.length === 0 && !error ? (
          <p className="mt-6 text-sm text-gray-500">No teams yet. Create a team from the Teams tab first.</p>
        ) : (
          <ul className="mt-4 max-h-64 space-y-2 overflow-y-auto" role="list">
            {teams.map((team) => {
              const checked = selected.has(team.id);
              return (
                <li key={team.id}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 px-3 py-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(team.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">{team.name}</span>
                      <span className="block font-mono text-xs text-gray-500">@{team.shortCode}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {error ? (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || loading || !dirty}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => void onSave()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
